import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, ArrowRight, Target, ListNumbers, Info, Tag } from "@phosphor-icons/react";

/**
 * TemplatePreviewDialog — read-only preview of a questionnaire.
 * Used from three places:
 *  1. TemplateBuilder — live preview of the draft ("Aperçu patient")
 *  2. Templates library page — preview a validated template before importing
 *  3. TemplateLibraryDialog (builder modal) — same, when browsing
 *
 * When `onUse` is provided, a primary "Utiliser ce modèle" CTA is shown in the
 * header and the scoring/interpretation block is rendered after the questions.
 */
export default function TemplatePreviewDialog({ open, template, onClose, onUse, useLabel = "Utiliser ce modèle", useBusy = false, headerLabel }) {
  if (!template) return null;
  const questions = template.questions || [];
  const scoring = template.scoring || null;
  const dimensions = scoring?.dimensions || [];
  const globalInterp = scoring?.interpretation || [];

  const scoringTypeLabel = {
    sum: "Somme des scores",
    percentage: "Pourcentage",
    none: "Aucun",
  }[scoring?.type] || null;

  const renderQuestion = (q, idx) => {
    const opts = q.options || [];
    return (
      <div key={q.id || idx} className="bg-white border border-[#E1E9F0] rounded-[14px] p-6">
        <div className="flex items-baseline gap-2.5 mb-4">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-[8px] bg-[#1E3A5F] text-white text-[13px] font-bold tabular-nums">
            {idx + 1}
          </span>
          <span className="text-[11px] tracking-[0.16em] uppercase text-[#5F758F] font-semibold">Question {idx + 1} / {questions.length}</span>
        </div>
        <h3 className="text-xl md:text-2xl font-bold tracking-tight text-[#1E3A5F] leading-snug mb-4">
          {q.label || <span className="text-[#5F758F] italic">Intitulé de la question</span>}
          {q.required && <span className="text-[#D45A3F]"> *</span>}
        </h3>
        {q.instructions && (
          <div className="mb-5 rounded-[10px] bg-[#FFF3D6] border border-[#F0DDA8] px-4 py-2.5">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#B76A00] font-semibold mb-1">Consigne clinicien</p>
            <p className="text-[13px] text-[#7A4C0B] leading-relaxed">{q.instructions}</p>
          </div>
        )}
        {q.type === "single_choice" && (
          <div className="space-y-2">
            {opts.map((o, oi) => (
              <div key={`sc-${oi}-${o.value ?? o.label ?? ""}`} className="border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-[#1E3A5F] flex items-center justify-between">
                <span>{o.label || <span className="text-[#5F758F] italic">option {oi + 1}</span>}</span>
                {onUse && typeof o.value === "number" && (
                  <span className="shrink-0 ml-3 inline-flex items-center justify-center min-w-[46px] px-2 py-0.5 rounded-full bg-[#E6F1F5] text-[#3A8FA5] font-semibold text-[11px] tabular-nums">
                    {o.value} pt{o.value > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {q.type === "multiple_choice" && (
          <div className="space-y-2">
            {opts.map((o, oi) => (
              <div key={`mc-${oi}-${o.value ?? o.label ?? ""}`} className="border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-[#1E3A5F] flex items-center gap-3">
                <span className="w-4 h-4 border-2 border-[#B7C4D6] rounded-sm shrink-0" />
                {o.label || <span className="text-[#5F758F] italic">option {oi + 1}</span>}
              </div>
            ))}
          </div>
        )}
        {q.type === "yes_no" && (
          <div className="flex gap-3">
            <div className="flex-1 border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-center text-[#1E3A5F]">
              Oui{onUse && q.score_weight ? <span className="ml-2 text-[11px] text-[#3A8FA5] font-semibold">+{q.score_weight}</span> : null}
            </div>
            <div className="flex-1 border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-center text-[#1E3A5F]">
              Non{onUse && q.score_weight ? <span className="ml-2 text-[11px] text-[#5F758F] font-semibold">0</span> : null}
            </div>
          </div>
        )}
        {q.type === "scale" && (
          <div>
            <div className="flex justify-between text-xs text-[#5F758F] mb-2">
              <span>{q.min_label || `Min (${q.min ?? 0})`}</span>
              <span>{q.max_label || `Max (${q.max ?? 10})`}</span>
            </div>
            <div className="h-3 bg-[#E1E9F0] rounded-full relative">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-[#3A8FA5] rounded-full" />
            </div>
          </div>
        )}
        {q.type === "likert" && (
          <div className="space-y-2">
            {["Pas du tout d'accord", "Plutôt pas d'accord", "Neutre", "Plutôt d'accord", "Tout à fait d'accord"].map((l) => (
              <div key={l} className="border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-[#1E3A5F]">{l}</div>
            ))}
          </div>
        )}
        {q.type === "text" && (
          <div className="border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-[#5F758F] italic">Réponse libre du patient…</div>
        )}
        {q.type === "date" && (
          <div className="border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-[#5F758F] italic">JJ/MM/AAAA</div>
        )}
        {q.type === "number" && (
          <div className="border border-[#E1E9F0] rounded-[10px] px-4 py-2.5 text-[14px] text-[#5F758F] italic">Nombre…</div>
        )}
      </div>
    );
  };

  const SectionTitle = ({ icon: Icon, label, count }) => (
    <div className="flex items-center gap-2.5 mb-4 mt-10 first:mt-0">
      <Icon size={16} weight="duotone" className="text-[#3A8FA5]" />
      <h2 className="text-[11px] tracking-[0.16em] uppercase text-[#5F758F] font-semibold">{label}</h2>
      {typeof count === "number" && (
        <span className="text-[12px] text-[#5F758F] tabular-nums">{count}</span>
      )}
      <span className="h-px flex-1 bg-[#E1E9F0]" />
    </div>
  );

  const renderScoringBlock = () => {
    if (!scoring || scoring.type === "none") return null;
    return (
      <>
        <SectionTitle icon={Target} label="Scoring & interprétation" />
        <div className="rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] px-4 py-3 mb-5">
          <p className="text-[13px] text-[#1E3A5F]">
            Mode : <strong>{scoringTypeLabel}</strong>
            {scoring.max_per_question ? <> · Poids max par question : <strong>{scoring.max_per_question}</strong></> : null}
          </p>
        </div>

        {dimensions.length > 0 && (
          <div className="space-y-4 mb-5">
            {dimensions.map((d) => (
              <div key={d.key} className="bg-white border border-[#E1E9F0] rounded-[14px] p-5">
                <p className="text-[15px] font-bold text-[#1E3A5F] mb-1">{d.label}</p>
                <p className="text-[12px] text-[#5F758F] mb-4">{d.question_ids?.length || 0} question{(d.question_ids?.length || 0) > 1 ? "s" : ""}</p>
                {(d.interpretation || []).length > 0 && (
                  <div className="space-y-1.5">
                    {d.interpretation.map((r, i) => (
                      <div key={`di-${d.key}-${r.min}-${r.max}-${i}`} className="flex items-baseline gap-3 text-[13px]">
                        <span className="shrink-0 inline-flex items-center justify-center min-w-[74px] px-2 py-0.5 rounded-full bg-[#E6F1F5] text-[#3A8FA5] font-semibold tabular-nums text-[12px]">
                          {r.min}–{r.max}
                        </span>
                        <span className="text-[#1E3A5F]">{r.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {globalInterp.length > 0 && (
          <div className="bg-white border border-[#E1E9F0] rounded-[14px] p-5">
            <p className="text-[15px] font-bold text-[#1E3A5F] mb-4">Paliers d'interprétation globaux</p>
            <div className="space-y-1.5">
              {globalInterp.map((r, i) => (
                <div key={`gi-${r.min}-${r.max}-${i}`} className="flex items-baseline gap-3 text-[13px]">
                  <span className="shrink-0 inline-flex items-center justify-center min-w-[74px] px-2 py-0.5 rounded-full bg-[#E6F1F5] text-[#3A8FA5] font-semibold tabular-nums text-[12px]">
                    {r.min}{r.min !== r.max ? `–${r.max}` : ""}
                  </span>
                  <span className="text-[#1E3A5F]">{r.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#F4F7FB] max-w-3xl max-h-[90vh] overflow-hidden p-0 gap-0" data-testid="preview-modal">
        <DialogHeader className="bg-white px-6 py-4 pr-14 border-b border-[#E1E9F0] shrink-0">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold tracking-tight text-[#1E3A5F]">
              <Eye size={20} weight="duotone" className="text-[#3A8FA5]" />
              {headerLabel || "Aperçu patient"}
            </DialogTitle>
            {onUse && (
              <Button
                size="sm"
                onClick={onUse}
                disabled={useBusy}
                className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold h-9 px-4 shrink-0 mr-2"
                data-testid="preview-use-btn"
              >
                {useBusy ? "Ajout…" : useLabel}
                <ArrowRight size={14} className="ml-1.5" />
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="overflow-y-auto px-8 py-8" style={{ maxHeight: "calc(90vh - 68px)" }}>
          {/* Identification block */}
          <div className="rounded-[14px] bg-white border border-[#E1E9F0] p-6 mb-2">
            {template.category && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6F1F5] border border-[#C7DEE6] text-[11px] font-semibold text-[#3A8FA5] mb-3">
                <Tag size={11} weight="fill" /> {template.category}
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1E3A5F] leading-tight mb-3">
              {template.title || <span className="text-[#5F758F] italic">Titre du questionnaire</span>}
            </h1>
            {template.description && (
              <p className="text-[14px] text-[#5F758F] leading-relaxed">{template.description}</p>
            )}
          </div>

          {/* General instructions */}
          {template.instructions && (
            <>
              <SectionTitle icon={Info} label="Consignes générales" />
              <div className="rounded-[14px] bg-[#FFF9E6] border border-[#F0DDA8] px-5 py-4">
                <p className="text-[14px] text-[#7A4C0B] leading-relaxed">{template.instructions}</p>
              </div>
            </>
          )}

          {/* Questions */}
          <SectionTitle icon={ListNumbers} label="Questions" count={questions.length} />
          <div className="space-y-3">
            {questions.length === 0 ? (
              <p className="text-[#5F758F] italic">Ajoutez au moins une question pour visualiser l'aperçu.</p>
            ) : questions.map(renderQuestion)}
          </div>

          {/* Scoring */}
          {onUse && renderScoringBlock()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
